// Aba "Emitir" da Planilha Gran: escolhe as faturas prontas (boleto calculado, ainda não
// emitido) e dispara a emissão em sequência.
//
// Por que em sequência e não num clique só: a emissão de fatura da parceria passa por
// BoletoEmissionModal, que faz ~2 mil linhas de conferência antes de falar com o Asaas
// (pagador, agrupamento, prazo, trava de reclique, rota Sicoob × Asaas). Refazer isso
// "por baixo" para o lote seria uma segunda cópia da regra de dinheiro. A fila abre essa
// mesma janela para cada fatura, e o próximo boleto abre sozinho quando o anterior sai.
import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckSquare, Info, Square, Ticket, ExternalLink } from 'lucide-react';
import ConfirmationModal from '../../../src/components/ConfirmationModal';
import type { LinhaPlanilhaGran } from './planilhaGranModel';

export type { ItemFilaGran } from './filaEmissaoGran';
import type { ItemFilaGran } from './filaEmissaoGran';

interface Props {
  /** Linhas do mês já recortadas por condomínio/origem/busca. */
  linhas: LinhaPlanilhaGran[];
  podeEmitir: boolean;
  onEmitir: (itens: ItemFilaGran[]) => void;
  onAbrirFatura: (faturaId: number) => void;
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const AbaEmitirGran: React.FC<Props> = ({ linhas, podeEmitir, onEmitir, onAbrirFatura }) => {
  const prontas = useMemo(() => linhas.filter(l => l.etapa === 'pronta' && l.faturaId !== null), [linhas]);
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [confirmar, setConfirmar] = useState(false);

  // Começa com as prontas que não pedem conferência; as que pedem ficam para o operador
  // olhar e marcar à mão. Refaz quando o conjunto muda (filtro, mês, realtime).
  const chaveProntas = prontas.map(l => l.faturaId).join(',');
  useEffect(() => {
    setMarcadas(new Set(prontas.filter(l => l.motivos.length === 0).map(l => l.faturaId as number)));
  }, [chaveProntas]);

  const grupos = useMemo(() => {
    const m = new Map<string, LinhaPlanilhaGran[]>();
    prontas.forEach(l => { const g = m.get(l.grupo); if (g) g.push(l); else m.set(l.grupo, [l]); });
    return Array.from(m.entries());
  }, [prontas]);

  // Esc fecha a Planilha Gran inteira (ver PlanilhaGranModal) — enquanto este
  // ConfirmationModal está aberto, Esc deve fechar só ele. Intercepta na fase de
  // captura, antes do listener global da planilha.
  useEffect(() => {
    if (!confirmar) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); setConfirmar(false); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [confirmar]);

  const selecionadas = prontas.filter(l => marcadas.has(l.faturaId as number));
  const totalPrevisto = selecionadas.reduce((a, l) => a + (l.boletoGran ?? 0), 0);

  const alternar = (ids: number[], marcar: boolean) => setMarcadas(prev => {
    const n = new Set(prev);
    ids.forEach(id => (marcar ? n.add(id) : n.delete(id)));
    return n;
  });

  if (!podeEmitir) {
    return (
      <div className="h-full flex items-center justify-center p-8 text-center text-sm text-gray-500 dark:text-gray-400">
        A emissão dos boletos é feita pelo Financeiro da Vigor.
      </div>
    );
  }

  if (prontas.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-2 p-8 text-center">
        <Ticket className="w-8 h-8 text-gray-300 dark:text-gray-600" aria-hidden="true" />
        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Nenhuma fatura pronta para emitir neste recorte</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 max-w-md">
          Aparecem aqui as faturas com boleto calculado e ainda não emitido. As que estão "Em andamento"
          precisam passar pela validação e pelo cálculo no quadro de faturas antes.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="mx-3 mt-3 px-3 py-2 rounded-lg bg-sky-50 dark:bg-sky-900/15 border border-sky-100 dark:border-sky-900/40 flex gap-2 text-[12px] text-sky-800 dark:text-sky-200">
        <Info className="w-4 h-4 shrink-0 mt-0.5" aria-hidden="true" />
        <p>
          Cada boleto abre na janela de emissão de sempre, com as mesmas conferências. Você confere e confirma;
          quando ele sai, o próximo abre sozinho. Dá para pular ou parar no meio. Os boletos ficam como
          "A enviar": nada vai para o cliente nesta etapa.
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {grupos.map(([grupo, ls]) => {
          const ids = ls.map(l => l.faturaId as number);
          const todas = ids.every(id => marcadas.has(id));
          return (
            <section key={grupo} className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <header className="px-3 py-2 bg-gray-50 dark:bg-gray-800/60 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => alternar(ids, !todas)}
                  className="inline-flex items-center gap-2 text-[13px] font-semibold text-gray-800 dark:text-gray-100 hover:text-primary"
                  aria-pressed={todas}
                >
                  {todas ? <CheckSquare className="w-4 h-4 text-primary" aria-hidden="true" /> : <Square className="w-4 h-4 text-gray-400" aria-hidden="true" />}
                  {grupo}
                </button>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">{ls.length} UC{ls.length !== 1 ? 's' : ''}</span>
              </header>
              <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                {ls.map(l => {
                  const id = l.faturaId as number;
                  const marcada = marcadas.has(id);
                  return (
                    <li key={id} className={`px-3 py-2 flex items-center gap-3 text-[13px] ${marcada ? 'bg-primary/5 dark:bg-primary/10' : ''}`}>
                      <label className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={marcada}
                          onChange={e => alternar([id], e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary/50"
                        />
                        <span className="font-mono text-gray-700 dark:text-gray-200 w-40 shrink-0">{l.numeroUc}</span>
                        <span className="truncate text-gray-500 dark:text-gray-400">{l.nomeCliente}</span>
                      </label>
                      {l.motivos.length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 dark:text-amber-300 max-w-xs truncate" title={l.motivos.join(' · ')}>
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" /> {l.motivos[0]}
                        </span>
                      )}
                      <span className="tabular-nums font-semibold text-gray-800 dark:text-gray-100 w-28 text-right" title="Valor previsto: energia + conta da Equatorial. O valor final é o que a janela de emissão confirmar.">
                        {l.boletoGran !== null ? brl(l.boletoGran) : '—'}
                      </span>
                      <button
                        type="button"
                        onClick={() => onAbrirFatura(id)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-gray-800"
                        title={`Abrir só a fatura ${id}`}
                        aria-label={`Abrir só a fatura ${id}`}
                      >
                        <ExternalLink className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </div>

      <footer className="shrink-0 px-4 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-3 bg-white dark:bg-gray-900">
        <button
          type="button"
          onClick={() => alternar(prontas.map(l => l.faturaId as number), selecionadas.length !== prontas.length)}
          className="text-[12px] font-medium text-gray-600 dark:text-gray-300 hover:text-primary"
        >
          {selecionadas.length === prontas.length ? 'Desmarcar todas' : `Marcar todas (${prontas.length})`}
        </button>
        <div className="flex-1 text-[12px] text-gray-600 dark:text-gray-300">
          <strong className="text-gray-900 dark:text-white">{selecionadas.length}</strong> de {prontas.length} selecionada{selecionadas.length !== 1 ? 's' : ''}
          {selecionadas.length > 0 && <> · previsto <strong className="text-gray-900 dark:text-white tabular-nums">{brl(totalPrevisto)}</strong></>}
        </div>
        <button
          type="button"
          disabled={selecionadas.length === 0}
          onClick={() => setConfirmar(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-[13px] font-semibold rounded-lg bg-primary hover:bg-primary/90 text-white disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Ticket className="w-4 h-4" aria-hidden="true" /> Emitir {selecionadas.length} em sequência
        </button>
      </footer>

      <ConfirmationModal
        isOpen={confirmar}
        onClose={() => setConfirmar(false)}
        onConfirm={() => {
          setConfirmar(false);
          onEmitir(selecionadas.map(l => ({ faturaId: l.faturaId as number, rotulo: `${l.grupo} · UC ${l.numeroUc}` })));
        }}
        title={`Emitir ${selecionadas.length} boleto${selecionadas.length !== 1 ? 's' : ''} em sequência?`}
        description={
          <span>
            A janela de emissão vai abrir para cada fatura, uma depois da outra. Cada boleto só é registrado no Asaas
            quando você confirmar na janela dele. Um boleto emitido só sai com cancelamento. Nada é enviado ao cliente
            nesta etapa.
          </span>
        }
        confirmText="Começar"
        variant="info"
      />
    </div>
  );
};

export default AbaEmitirGran;
